import { getPhantomHints } from "@/components/suggestions/providers/utils/getPhantomHints";
import { prioritizeSuggestionRules } from "@/input/utils/editOperators";
import { isUnitChild } from "@/models/nodes/utils";

import { candidates } from "./librarySuggestionsProvider/candidates";
import { existingSymbols } from "./librarySuggestionsProvider/existing";
import { isSlashMenuActive } from "./slashCommandSuggestionsProvider";
import { filterSuggestions } from "./utils/filterSuggestions";
import { injectNodePropsToSuggestion } from "./utils/injectNodePropsToSuggestion";
import { TEXT_SYMBOLS } from "./utils/operatorAutocomplete";

import type { ProviderSearchOptions, SuggestionProvider } from "@/components/suggestions/types";
import type { CopyNodePropsOptions } from "@/models/nodes/copyNodePropsFromDoc";

export { LIBRARY_SERVES } from "./librarySuggestionsProvider/candidates";
export { librarySuggestionBySymbolKey } from "./librarySuggestionsProvider/existing";
export { libraryItemToSuggestion } from "./librarySuggestionsProvider/toSuggestion";

/**
 * What a search asked for: every type when its accept list is empty or names `library`, else
 * the types it names; service functions only when it names `serviceFunction` or asks with
 * `service`.
 */
function requested(options: NonNullable<ProviderSearchOptions["library"]> = {}) {
  const accept = options.accept ?? [];
  const everyType = accept.length === 0 || accept.includes("library");

  return {
    types: everyType ? undefined : new Set<string>(accept),
    service: options.service || accept.includes("serviceFunction"),
  };
}

/**
 * The operator symbols whose row also shows what the hints would compute, as the typed provider
 * did.
 */
const PHANTOM_HINT_TRIGGER_SYMBOLS = new Set(["=", "∈"]);

export const librarySuggestionsProvider: SuggestionProvider<"library"> = {
  type: "library",
  search: (str, doc, lang, options) => {
    if (!doc.libraries.enabled || doc.selection.hasTextSelection) {
      return [];
    }

    const inUnit = isUnitChild(doc.cursor.node);
    const slashMenu = isSlashMenuActive(str, doc);
    const { filterExistingNodes } = options ?? {};
    const request = requested(options);
    const existing = existingSymbols(doc, filterExistingNodes);
    const suggestions = candidates(doc)
      .filter((candidate) => {
        if (!candidate.single) {
          return false;
        }

        const isOperator = candidate.type === "operatorSymbol";
        if (inUnit && !(isOperator && TEXT_SYMBOLS.includes(candidate.suggestion.item.symbol))) {
          return false;
        }

        if (isOperator && slashMenu) {
          return false;
        }

        if (candidate.service && !request.service) {
          return false;
        }

        if (request.types && !request.types.has(candidate.type)) {
          return false;
        }

        const { item } = candidate.suggestion;
        if (item.isSymbol) {
          return item.symbol !== str;
        }

        return !existing.get(candidate.type)?.has(item.symbol);
      })
      .map((candidate) => candidate.suggestion);

    let copyFrom: CopyNodePropsOptions["copyFrom"] | undefined;

    if (filterExistingNodes) {
      copyFrom = (node) => {
        return filterExistingNodes(node.value ?? "", node.type, new Set([node.id]));
      };
    }

    const inputMode = doc.editor.editorState.isExpression ? "math" : "text";
    const filtered = filterSuggestions(str, suggestions, {
      lang,
      inputMode,
      nodeLookup: doc.nodeLookup,
    });

    return [
      filtered.map((suggestion) => ({
        ...injectNodePropsToSuggestion(doc, suggestion, { copyFrom }),
        priority: prioritizeSuggestionRules(suggestion, doc.cursor),
        phantomHints: PHANTOM_HINT_TRIGGER_SYMBOLS.has(suggestion.item.symbol)
          ? getPhantomHints(doc, suggestion.item.symbol)
          : undefined,
      })),
    ];
  },
};
